import orderBy from 'lodash/orderBy'
import { action, computed, makeObservable, observable } from 'mobx'
import { dependsOn, trackedAction } from 'mobx-async'

import {
  BookingItemSummaryKind,
  DiscountParams,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert } from '@avantstay/backoffice-core'

import { SidePanelStore, SidePanelType } from '@new/__global/stores/SidePanelStore'
import { UiStore } from '@new/__global/stores/UiStore'
import { FolioView } from '@new/domains/bookings/config/folioViews'
import { BookingFolioChangeHistory } from '@new/domains/bookings/screens/bookingPayment/actionPanels/ItemHistory/ItemHistory.types'
import { BookingAdjustmentStore } from '@new/domains/bookings/stores/BookingAdjustmentStore'
import { BookingPaymentStore } from '@new/domains/bookings/stores/BookingPaymentStore'
import { BookingStore } from '@new/domains/bookings/stores/BookingStore'
import { BookingValueAddedServicesStore } from '@new/domains/bookings/stores/BookingValueAddedServicesStore'
import { RefundStore } from '@new/domains/bookings/stores/RefundStore'
import { parseFolioView } from '@new/domains/bookings/utils/parseFolioView'

import bookings, {
  bookingModificationPossibility,
  getBookingFolioInfo,
  getBookingItems,
  getOTAFolio,
} from '@arriere/modules/bookings'
import folio from '@arriere/modules/folio'
import { BookingModificationPossibilityView } from '@arriere/types/BookingFolio.types'
import { ROUTES } from '@routes'
import { computedRouteParam } from '@stores/computedRouteParam'
import { trackError, trackEvent } from '@tracking/customEventReport'

import { BookingFolioSummary } from '../_types'
import { PaymentDetailsActionOptions } from '../screens/bookingPayment/Summary/_types'
import {
  BookingFolioItemProps,
  BookingFolioSummaryProps,
  FilteredItems,
  ItemCancellationInput,
  PaymentAndServicesItems,
  UpdateCancelledBookingFeesInput,
} from './_types'

const sortBookingPaymentServicesByKey = (
  items: BookingFolioItemProps[],
  key: Extract<keyof BookingFolioItemProps, 'date' | 'createdAt'>,
) =>
  items.sort(
    (a, b) => Number(new Date(a[key] ?? '').getTime()) - Number(new Date(b[key] ?? '').getTime()),
  )

const sortNightArrayByDate = (array: BookingFolioItemProps[]): BookingFolioItemProps[] => {
  return array.sort(
    (a, b) => Number(new Date(a.date || '').getTime()) - Number(new Date(b.date || '').getTime()),
  )
}

const sortItemArrayByCreateAt = (array: BookingFolioItemProps[]): BookingFolioItemProps[] => {
  return array.sort(
    (a, b) => Number(new Date(a.createdAt).getTime()) - Number(new Date(b.createdAt).getTime()),
  )
}

export const handleSubmitError = (alert: string, extra?: any): void => {
  Alert.Error(alert)

  BookingPaymentStore.forceUpdateBookingPayments()

  trackError(`Booking Folio: ${alert}`, { extra })
}

class _BookingFolioStore {
  activeActionPanel: PaymentDetailsActionOptions | null = null

  cancelableItem: BookingFolioItemProps | null = null

  folioItem: { id: string; amount: number } = { id: '', amount: 0 }

  bookingId: string | null = null

  showBookingItemForPayment = false

  depositIdToEditRate: string = ''

  isReviewRequestsOpen = computedRouteParam<boolean | undefined>('isReviewRequestsOpen', {
    patterns: [ROUTES.bookingPaymentAndServices.path],
    parse: JSON.parse,
  })

  isManageBalanceOpen = computedRouteParam<boolean | undefined>('isManageBalanceOpen', {
    patterns: [ROUTES.bookingPaymentAndServices.path],
    parse: JSON.parse,
  })

  isMakeChargeOpen = computedRouteParam<boolean | undefined>('isMakeChargeOpen', {
    patterns: [ROUTES.bookingPaymentAndServices.path],
    parse: JSON.parse,
  })

  folioView = computedRouteParam<FolioView>('view', {
    patterns: [ROUTES.bookingPaymentAndServices.path],
    defaultValue: FolioView.internal,
    parse: parseFolioView,
  })

  constructor() {
    makeObservable(this, {
      activeActionPanel: observable,
      cancelableItem: observable,
      folioItem: observable,
      bookingId: observable,
      showBookingItemForPayment: observable,
      depositIdToEditRate: observable,
      adjustmentReasons: computed,
      mainEmailHasAccount: computed,
      bookingItems: computed,
      depositsOnHold: computed,
      discountsTotal: computed,
      deposits: computed,
      bookingBalanceSummary: computed,
      bookingFolioInfo: computed,
      bookingItemsFiltered: computed,
      bookingPaymentAndServicesItems: computed,
      bookingItemHistory: computed,
      walletBalance: computed,
      bookingOTAFolio: computed,
      bookingDepositToEdit: computed,
      setCancelableItem: action.bound,
      setActiveActionPanel: action.bound,
      setItemInfo: action.bound,
      setBookingId: action.bound,
      setShowBookingItemForPayment: action.bound,
      openPaymentServicesAdjustmentDrawer: action.bound,
      openAddNewDepositDrawer: action.bound,
      closePaymentServicesAdjustmentDrawer: action.bound,
      openPaymentServicesManagePaymentChargeDrawer: action.bound,
      openSaveNewCardDrawer: action.bound,
      closePaymentServicesManagePaymentChargeDrawer: action.bound,
      closeAddDepositDrawer: action.bound,
      closeIsReviewRequestsOpen: action.bound,
      closeIsManageBalanceOpen: action.bound,
      closeCancelAdjustmentDrawer: action.bound,
      openEditDepositDrawer: action.bound,
      closeEditDepositDrawer: action.bound,
    })

    this.sendBillNotification = trackedAction<any>(this.sendBillNotification.bind(this))
    this.refundDeposits = trackedAction<any>(this.refundDeposits.bind(this))
    this.adjustItemAmount = trackedAction<any>(this.adjustItemAmount.bind(this))
    this.discountItem = trackedAction<any>(this.discountItem.bind(this))
    this.bookingModificationPossibility = trackedAction<any>(
      this.bookingModificationPossibility.bind(this),
    )
    this.cancelDeposits = trackedAction<any>(this.cancelDeposits.bind(this))
    this.cancelItems = trackedAction<any>(this.cancelItems.bind(this))
    this.addAdjustment = trackedAction<any>(this.addAdjustment.bind(this))
    this.updateCancelledBookingFees = trackedAction<any>(this.updateCancelledBookingFees.bind(this))
    this.addDepositNoReload = trackedAction<any>(this.addDepositNoReload.bind(this))
    this.addDeposit = trackedAction<any>(this.addDeposit.bind(this))
  }

  get adjustmentReasons() {
    return bookings.getAdjustmentReasons()
  }

  get mainEmailHasAccount(): Promise<boolean> {
    return BookingStore.booking.then(booking => {
      if (!booking) return false

      return folio.checkIfUserExists(booking.emails[0])
    })
  }

  get bookingItems(): Promise<BookingFolioItemProps[]> {
    dependsOn(
      BookingStore.booking,
      BookingPaymentStore.makeBookingCardCharge,
      // BookingPaymentStore.makeSecurityDepositCharge,
      BookingPaymentStore.makeFolioSecurityDepositCharge,
      BookingPaymentStore.makeWholesaleCharge,
      BookingPaymentStore.makeWalletCharge,
      RefundStore.refundPayments,
      BookingValueAddedServicesStore.makeCharge,
      BookingValueAddedServicesStore.cancelServiceRequest,
      BookingAdjustmentStore.approveBookingRequests,
      BookingAdjustmentStore.processBookingRequests,
      BookingAdjustmentStore.rejectBookingRequests,
      BookingAdjustmentStore.refundPaymentMethods,
      BookingAdjustmentStore.requestBookingAdjustment,
      this.addDeposit,
      this.addAdjustment,
      this.cancelItems,
      this.updateCancelledBookingFees,
      this.adjustItemAmount,
      this.discountItem,
      this.refundDeposits,
      this.cancelDeposits,
    )

    return BookingStore.booking
      .then(booking => (booking?.id ? getBookingItems(booking.id) : Promise.resolve([])))
      .then(_bookingItems => _bookingItems)
  }

  get depositsOnHold(): Promise<number> {
    return this.bookingItemsFiltered.then(({ depositsOnHold }) => depositsOnHold)
  }

  get discountsTotal(): Promise<number> {
    return this.bookingItemsFiltered.then(({ discountsTotal }) => discountsTotal)
  }

  get deposits(): Promise<FilteredItems['deposits']> {
    return this.bookingItemsFiltered.then(({ deposits }) => deposits)
  }

  get bookingBalanceSummary(): Promise<BookingFolioSummary | undefined> {
    dependsOn(
      this.bookingItems,
      this.adjustItemAmount,
      this.addAdjustment,
      RefundStore.refundPaymentMethods,
      BookingPaymentStore.deleteBookingCharge,
      BookingAdjustmentStore.approveBookingRequests,
      BookingAdjustmentStore.processBookingRequests,
      BookingAdjustmentStore.rejectBookingRequests,
      BookingAdjustmentStore.refundPaymentMethods,
      BookingAdjustmentStore.requestBookingAdjustment,
      BookingFolioStore.cancelItems,
      BookingFolioStore.updateCancelledBookingFees,
    )

    return BookingStore.booking.then(booking => {
      return booking?.id
        ? bookings.getBookingBalanceSummary(booking.id)
        : Promise.resolve(undefined)
    })
  }

  get bookingFolioInfo(): Promise<BookingFolioSummaryProps | undefined> {
    const { bookingId } = this
    if (!bookingId) return Promise.resolve(undefined)
    return getBookingFolioInfo(bookingId)
  }

  get bookingItemsFiltered(): Promise<FilteredItems> {
    dependsOn(this.bookingItems)

    return this.bookingItems.then(_bookingItems => {
      const filteredItems: FilteredItems = {
        cancelled: [],
        nights: [],
        fees: [],
        taxes: [],
        services: [],
        deposits: [],
        adjustments: [],
        discountsTotal: 0,
        depositsOnHold: 0,
      }

      const organizeItemByKind = (
        item: BookingFolioItemProps,
      ): Partial<Record<BookingItemSummaryKind, () => number>> => ({
        [BookingItemSummaryKind.night]: () => filteredItems.nights.push(item),
        [BookingItemSummaryKind.fee]: () => filteredItems.fees.push(item),
        [BookingItemSummaryKind.tax]: () => filteredItems.taxes.push(item),
        [BookingItemSummaryKind.service]: () => filteredItems.services.push(item),
        [BookingItemSummaryKind.deposit]: () => filteredItems.deposits.push(item),
        [BookingItemSummaryKind.adjustment]: () => filteredItems.adjustments.push(item),
      })

      _bookingItems?.forEach((item: BookingFolioItemProps) => {
        if (item.adjustment) {
          filteredItems.discountsTotal += item.adjustment.amount
        }

        if (item.cancellation) {
          filteredItems.cancelled.push(item)

          if (item.kind === BookingItemSummaryKind.deposit) {
            filteredItems.deposits.push(item)
          }
        } else {
          // Saves the item in closure
          const listOfKinds = organizeItemByKind(item)
          // Executes the push for that kind
          listOfKinds[item.kind]?.()
        }

        if (item.kind === BookingItemSummaryKind.deposit) {
          filteredItems.depositsOnHold += item?.balance ?? 0
        }
      })

      const itemKeys = Object.keys(filteredItems) as Array<keyof typeof filteredItems>

      itemKeys.forEach(key => {
        if (Array.isArray(filteredItems[key])) {
          if (key === 'nights') {
            filteredItems[key] = sortNightArrayByDate(filteredItems[key])
            // Doing this extra validation to ensure we don't assign an array to a numeric property
          } else if (key !== 'discountsTotal' && key !== 'depositsOnHold') {
            filteredItems[key] = sortItemArrayByCreateAt(filteredItems[key])
          }
        }
      })

      return filteredItems
    })
  }

  get bookingPaymentAndServicesItems(): Promise<PaymentAndServicesItems> {
    dependsOn(this.bookingItems)

    return this.bookingItems.then(_bookingItems => {
      const items = _bookingItems?.reduce(
        (acc, item) => {
          if (item.adjustment) {
            acc.discountsTotal += item.adjustment.amount
          }

          switch (item.kind) {
            case BookingItemSummaryKind.night:
              acc.nights.push(item)
              break
            case BookingItemSummaryKind.tax:
              acc.taxes.push(item)
              break
            case BookingItemSummaryKind.fee:
              acc.fees.push(item)
              break
            case BookingItemSummaryKind.service:
              acc.services.push(item)
              break
            case BookingItemSummaryKind.deposit:
              acc.deposits.push(item)
              acc.depositsOnHold += item.total
              break
            case BookingItemSummaryKind.adjustment:
              acc.adjustments.push(item)
              break
            default:
              break
          }

          return acc
        },
        {
          nights: [],
          taxes: [],
          fees: [],
          services: [],
          deposits: [],
          adjustments: [],
          discountsTotal: 0,
          depositsOnHold: 0,
        } as PaymentAndServicesItems,
      )

      const itemKeys = (items ? Object.keys(items) : []) as Array<keyof typeof items>

      itemKeys?.forEach(key => {
        if (Array.isArray(items[key])) {
          const sortKey = key === 'nights' ? 'date' : 'createdAt'

          // Doing this extra validation to ensure we don't assign an array to a numeric property
          if (key !== 'discountsTotal' && key !== 'depositsOnHold') {
            items[key] = sortBookingPaymentServicesByKey(items[key], sortKey)
          }
        }
      })

      return items
    })
  }

  get bookingItemHistory(): Promise<BookingFolioChangeHistory[]> {
    dependsOn(this.bookingItems, this.setItemInfo)

    const itemId = this.folioItem?.id ?? ''
    return BookingStore.booking
      .then(booking => {
        if (!booking) return Promise.resolve([])

        return bookings.getBookingItemHistory(booking.id, itemId)
      })
      .then(result => orderBy([...result], ['time'], ['desc']))
  }

  get walletBalance(): Promise<number | undefined> {
    return BookingStore.booking.then(booking => {
      if (!booking) return Promise.resolve(undefined)

      return folio.walletBalance(booking.guestId)
    })
  }

  get bookingOTAFolio() {
    return BookingStore.booking.then(booking => {
      if (!booking) return Promise.resolve(undefined)

      return getOTAFolio(booking.id)
    })
  }

  get bookingDepositToEdit(): Promise<BookingFolioItemProps | undefined> {
    void [this.depositIdToEditRate]

    return this.deposits.then(deposits =>
      deposits.find(deposit => deposit.id === this.depositIdToEditRate),
    )
  }

  // Actions

  setCancelableItem(item: BookingFolioItemProps | null): void {
    this.cancelableItem = item
  }

  setActiveActionPanel(option: PaymentDetailsActionOptions | null): void {
    this.activeActionPanel = option
  }

  setItemInfo({ id, amount }: { id: string; amount: number }): void {
    this.folioItem = { id, amount }
  }

  setBookingId(id: string): void {
    this.bookingId = id
  }

  setShowBookingItemForPayment(show: boolean): void {
    this.showBookingItemForPayment = show
  }

  openPaymentServicesAdjustmentDrawer(): void {
    SidePanelStore.setCurrentSidePanel(SidePanelType.paymentAndServicesAdjustments)
  }

  openAddNewDepositDrawer(): void {
    SidePanelStore.setCurrentSidePanel(SidePanelType.addNewSecurityDeposit)
  }

  closePaymentServicesAdjustmentDrawer(): void {
    UiStore.setShouldDisplayFilterSideBar(false)
    SidePanelStore.setCurrentSidePanel()
  }

  openPaymentServicesManagePaymentChargeDrawer(): void {
    this.setShowBookingItemForPayment(true)
    SidePanelStore.setCurrentSidePanel(SidePanelType.paymentAndServicesCharge)
  }

  openSaveNewCardDrawer(): void {
    SidePanelStore.setCurrentSidePanel(SidePanelType.paymentAndServicesSaveNewCard)
  }

  closePaymentServicesManagePaymentChargeDrawer(): void {
    this.setShowBookingItemForPayment(false)
    UiStore.setShouldDisplayFilterSideBar(false)
    SidePanelStore.setCurrentSidePanel()
  }

  closeAddDepositDrawer(): void {
    UiStore.setShouldDisplayFilterSideBar(false)
    SidePanelStore.setCurrentSidePanel()
  }

  closeIsReviewRequestsOpen(): void {
    this.isReviewRequestsOpen.push(null)
  }

  closeIsManageBalanceOpen(): void {
    this.isManageBalanceOpen.push(null)
  }

  async addDeposit(title: string, total: number): Promise<boolean> {
    trackEvent('Booking Folio: Attempting to add deposit', { title, total })
    return BookingStore.booking
      .then(booking => {
        if (!booking) {
          return Promise.reject(new Error('Booking ID is required to add a deposit.'))
        }

        return folio.addDeposit(booking.id, title, total)
      })
      .then(success => {
        if (success) {
          Alert.Success('Deposit added successfully.')
          trackEvent('Booking Folio: Deposit added successfully!', {
            title,
            total,
          })
        } else {
          handleSubmitError('An error occurred while adding a deposit.')
        }
        return success
      })
  }

  async addDepositNoReload(title: string, total: number): Promise<boolean> {
    return BookingStore.booking
      .then(booking => {
        if (!booking) {
          return Promise.reject(new Error('Booking ID is required to add a deposit.'))
        }

        return folio.addDeposit(booking.id, title, total)
      })
      .then(success => {
        if (!success) {
          handleSubmitError('An error occurred while adding a deposit.')
        }
        return success
      })
  }

  async addAdjustment(reason: string, amount: number): Promise<boolean> {
    return BookingStore.booking
      .then(booking => {
        if (!booking) {
          return Promise.reject(new Error('Booking ID is required to add an adjustment.'))
        }

        return folio.addAdjustment({
          bookingId: booking.id,
          reason,
          amount,
        })
      })
      .then(success => {
        if (success) {
          BookingStore.updateBookingValuesFromServer()
          Alert.Success('Item added successfully.')
        } else {
          handleSubmitError('An error occurred while adding an item.')
        }
        return success
      })
  }

  async updateCancelledBookingFees(input: UpdateCancelledBookingFeesInput): Promise<boolean> {
    return folio
      .updateCancelledBookingFees(input)
      .then(result => {
        BookingStore.updateBookingValuesFromServer()
        Alert.Success('Cancellation fee updated.')
        return !!result
      })
      .catch(() => {
        handleSubmitError('An error occurred while updating the cancellation fee.')
        return false
      })
  }

  async cancelItems(items: string[], reason: string): Promise<boolean> {
    trackEvent('Cancel deposit: Attempting to cancel the deposit.', {
      depositId: items.join(', '),
    })

    return BookingStore.booking
      .then(booking => {
        if (!booking) {
          return Promise.reject(new Error('Booking ID is required to cancel items.'))
        }

        return folio.cancelItems(booking.id, items, reason)
      })
      .then(success => {
        if (success) {
          Alert.Success('Item cancelled successfully.')
        } else {
          handleSubmitError('An error occurred while canceling an item.')
        }
        return success
      })
      .catch(error => {
        // The backend refuses to cancel some items; without this the rejection only reached the
        // console and the panel looked like it had done nothing.
        handleSubmitError(error?.message || 'An error occurred while canceling an item.', error)
        return false
      })
  }

  async cancelDeposits(items: ItemCancellationInput[]) {
    try {
      const booking = await BookingStore.booking

      if (!booking) {
        return Promise.reject(new Error('Booking ID is required to cancel deposits.'))
      }

      const success = await folio.cancelDeposits(booking.id, items)

      return success
        ? Promise.resolve(true)
        : Promise.reject(new Error('We were not able to adjust requested service rate'))
    } catch (error) {
      throw new Error(`Error: ${error}`)
    }
  }

  async bookingModificationPossibility(
    bookingId?: string,
  ): Promise<BookingModificationPossibilityView | undefined> {
    if (bookingId) {
      return bookingModificationPossibility(bookingId)
    }

    return Promise.resolve(undefined)
  }

  async discountItem(itemId: string, discount: DiscountParams): Promise<boolean> {
    return BookingStore.booking
      .then(booking => {
        if (!booking) {
          return Promise.reject(new Error('Booking ID is required to discount an item.'))
        }

        return folio.discountItem(booking.id, itemId, discount)
      })
      .then(success => {
        if (success) {
          Alert.Success('Discount added successfully.')
        } else {
          handleSubmitError('An error occurred while adding a discount.')
        }
        return success
      })
  }

  async adjustItemAmount(itemId: string, amount: number, reason: string): Promise<boolean> {
    return BookingStore.booking
      .then(booking => {
        if (!booking) {
          return Promise.reject(new Error('Booking ID is required to adjust item amount.'))
        }

        return folio.adjustItem(booking.id, itemId, amount, reason)
      })
      .then(success => {
        if (success) {
          Alert.Success('Refund added successfully.')
          BookingStore.updateBookingValuesFromServer()
        } else {
          handleSubmitError('An error occurred when adding a refund.')
        }
        return success
      })
  }

  async refundDeposits(): Promise<boolean> {
    return BookingStore.booking
      .then(booking => {
        if (!booking) {
          return Promise.reject(new Error('Booking ID is required to refund deposits.'))
        }

        return folio.refundDeposits(booking.id)
      })
      .then(success => {
        if (success) {
          Alert.Success('Deposits have been refunded successfully.')
        } else {
          handleSubmitError('An error occurred while refunding deposits.')
        }
        return success
      })
  }

  async sendBillNotification({
    bookingId,
    email,
    phone,
  }: {
    bookingId: string
    email: string
    phone: string
  }): Promise<boolean> {
    return folio.sendBillNotification(bookingId, email, phone).then(success => {
      if (success) {
        Alert.Success('The bill notification has been sent successfully.')
      } else {
        handleSubmitError('An error occurred while sending the bill notification.')
      }
      return success
    })
  }

  closeCancelAdjustmentDrawer(): void {
    UiStore.setShouldDisplayFilterSideBar(false)
    SidePanelStore.setCurrentSidePanel()
  }

  async openEditDepositDrawer(depositId: string) {
    if (SidePanelStore.currentSidePanel && this.depositIdToEditRate === depositId) {
      return
    }

    if (SidePanelStore.currentSidePanel && this.depositIdToEditRate !== depositId) {
      this.closeEditDepositDrawer()
    }

    this.depositIdToEditRate = depositId
    SidePanelStore.setCurrentSidePanel(SidePanelType.paymentAndServicesEditDeposit)
  }

  closeEditDepositDrawer(): void {
    this.depositIdToEditRate = ''
    this.closeCancelAdjustmentDrawer()
  }
}

export const BookingFolioStore = new _BookingFolioStore()
